import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchServerCart,
  updateServerItem,
  removeServerItem,
  clearServerCart,
  mergeLocalToServer
} from "../services/CartServices";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import "./CartPage.css";

const CartPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔹 EXISTING 333
  const [isCombo333, setIsCombo333] = useState(
    localStorage.getItem("isCombo333") === "true"
  );

  // 🔹 NEW YEAR 419 (AUTO)
  const [isCombo419, setIsCombo419] = useState(
    localStorage.getItem("isNewYearCombo419") === "true"
  );

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.info("Please log in to view your cart");
      navigate("/login");
      return;
    }
    loadCart();
    const onUpdate = () => loadCart();
    window.addEventListener("cartUpdated", onUpdate);
    return () => window.removeEventListener("cartUpdated", onUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCart = async () => {
    setLoading(true);
    try {
      await mergeLocalToServer?.();

      // ✅ decide combo automatically
      const comboParam = isCombo419
        ? { combo: 419 }
        : isCombo333
        ? { combo: 333 }
        : {};

      const res = await fetchServerCart(comboParam);

      let serverItems = [];
      if (Array.isArray(res)) serverItems = res;
      else if (res?.items) serverItems = res.items;
      else if (res?.data?.items) serverItems = res.data.items;

      setItems(serverItems || []);

      // ---------- AUTO VALIDATE ₹333
      if (isCombo333) {
        const valid333 =
          serverItems.length === 3 &&
          serverItems.every((it) => (it.quantity || 1) === 1);

        if (!valid333) {
          localStorage.removeItem("isCombo333");
          setIsCombo333(false);
          toast.error("₹333 combo removed — needs 3 items");
        }
      }

      // ---------- AUTO VALIDATE ₹419
      if (isCombo419) {
        const valid419 =
          serverItems.length === 4 &&
          serverItems.every((it) => (it.quantity || 1) === 1);

        if (!valid419) {
          localStorage.removeItem("isNewYearCombo419");
          setIsCombo419(false);
          toast.error("₹419 combo removed — needs 4 items");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not load cart");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const isAnyCombo = isCombo333 || isCombo419;

  const changeQty = async (item, qty) => {
    if (isAnyCombo) {
      toast.warn("Quantity cannot be changed in combo offer");
      return;
    }
    if (qty < 1) return;

    // ✅ Check if bulk discount is now active (qty >= 2)
    if (qty >= 2 && item.quantity < 2) {
      const unitPrice = item.unitPrice || 0;
      const itemTotal = item.itemTotal || 0;
      const fullPrice = unitPrice * qty;
      const discount = fullPrice - itemTotal;

      if (discount > 0) {
        toast.success(
          `🎉 Bulk Discount Applied! Save ₹${discount.toFixed(2)} on ${item.Product?.name || item.GiftProduct?.name}`,
          { autoClose: 3000 }
        );
      }
    }

    await updateServerItem(item.id, qty);
    await loadCart();
  };

  const removeItem = async (item) => {
    await removeServerItem(item.id);
    await loadCart();
  };

  const clearAll = async () => {
    await clearServerCart();
    localStorage.removeItem("isCombo333");
    localStorage.removeItem("isNewYearCombo419");
    setIsCombo333(false);
    setIsCombo419(false);
    toast.warn("Cart cleared");
    await loadCart();
  };

  const checkout = () => {
    if (!items.length) {
      toast.error("Cart is empty");
      return;
    }
    navigate(
      "/checkout" +
        (isCombo419 ? "?combo=419" : isCombo333 ? "?combo=333" : "")
    );
  };

  // ---------------- PRICE LOGIC ----------------
  // subtotal is calculated from backend-provided itemTotal
  let subtotal = items.reduce((s, it) => s + Number(it.itemTotal || 0), 0);

  let deliveryCharge = subtotal < 100 && subtotal > 0 ? 50 : 0;
  let total = subtotal + deliveryCharge;

  const valid333 =
    isCombo333 &&
    items.length === 3 &&
    items.every((it) => (it.quantity || 1) === 1);

  const valid419 =
    isCombo419 &&
    items.length === 4 &&
    items.every((it) => (it.quantity || 1) === 1);

  const comboUnitPrice = valid333
    ? 333 / 3
    : valid419
    ? 419 / 4
    : null;

  if (valid333) {
    subtotal = 333;
    total = 333;
    deliveryCharge = 0;
  }

  if (valid419) {
    subtotal = 419;
    total = 419;
    deliveryCharge = 0;
  }
  // ---------------------------------------------
  return (
    <div className="rn-cart-page">
      {/* Gradient Background */}
      <div className="rn-gradient-bg" />

      <motion.div
        className="rn-cart-wrap"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <header className="rn-cart-header">
          <h1>🛍️ Roupan Naturals Cart</h1>
          <p className="rn-subtitle">Pure • Organic • Handmade</p>
        </header>

        {valid333 && (
          <motion.div
            className="combo-banner combo-333"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            🎉 <b>₹333 Combo Applied</b> — 3 Products
          </motion.div>
        )}

        {valid419 && (
          <motion.div
            className="combo-banner combo-419"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            🎆 <b>New Year ₹419 Combo Applied</b> — 4 Products
          </motion.div>
        )}

        {loading ? (
          <div className="rn-loading">Loading cart…</div>
        ) : items.length === 0 ? (
          <div className="rn-empty">Your cart is empty 🌿</div>
        ) : (
          <>
            <div className="rn-items">
              <AnimatePresence>
                {items.map((it) => (
                  <motion.div
                    key={it.id}
                    className="rn-item-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      className="rn-item-image"
                      src={
                        it.Product?.image ||
                        it.GiftProduct?.imageUrl ||
                        "/assets/default-product.jpg"
                      }
                      alt="product"
                    />

                    <div className="rn-item-body">
                      <h3>{it.Product?.name || it.GiftProduct?.name}</h3>

                      <div className="rn-item-meta">
                        <span className="price">
                          ₹
                          {comboUnitPrice
                            ? comboUnitPrice.toFixed(2)
                            : Number(it.unitPrice || 0).toFixed(2)}
                        </span>

                        <div className="rn-qty-control">
                          <button
                            className="qty-btn qty-minus"
                            onClick={() => changeQty(it, (it.quantity || 1) - 1)}
                            disabled={isAnyCombo || (it.quantity || 1) <= 1}
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={it.quantity || 1}
                            disabled={isAnyCombo}
                            onChange={(e) => changeQty(it, Number(e.target.value))}
                          />
                          <button
                            className="qty-btn qty-plus"
                            onClick={() => changeQty(it, (it.quantity || 1) + 1)}
                            disabled={isAnyCombo}
                          >
                            +
                          </button>
                        </div>

                        <span className="item-total">
                          ₹{comboUnitPrice ? comboUnitPrice.toFixed(2) : Number(it.itemTotal || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <button
                      className="btn rn-btn rn-btn-danger"
                      onClick={() => removeItem(it)}
                    >
                      Remove
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <footer className="rn-cart-footer">
              <div className="amount">Subtotal: ₹{subtotal.toFixed(2)}</div>
              <div className="amount">
                Delivery: {deliveryCharge === 0 ? "Free" : `₹${deliveryCharge}`}
              </div>

              {/* ✅ DELIVERY NOTE ADDED */}
              <div className="rn-delivery-note">
                🚚 Orders below <b>₹100</b> will be charged <b>₹50</b> delivery fee
              </div>

              <div className="amount total">
                Total: ₹{total.toFixed(2)}
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn rn-btn rn-btn-primary"
                onClick={checkout}
              >
                Proceed to Checkout
              </motion.button>
            </footer>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default CartPage;
